import { useState, useEffect } from 'react';
import { usePartner } from '../../../../context/partnerManagement/PartnerContext';
import { extractCoordinatesFromMapUrl } from '../../utils/partnerHelpers';
import CategorySelector from './sections/CategorySelector/CategorySelector';
import BusinessLogoSection from './sections/BusinessLogoSection/BusinessLogoSection';
import BasicInfoSection from './sections/BasicInfoSection/BasicInfoSection';
import CategoryDetails from './sections/CategoryDetails/CategoryDetails';
import BusinessHoursSection from './sections/BusinessHoursSection/BusinessHoursSection';
import MediaGallerySection from './sections/MediaGallerySection/MediaGallerySection';

const AddPartnerForm = ({ onSuccess, initialData }) => {
  const { locations, isSubmitting, uploadImages, registerPartner, updatePartner } = usePartner();

  const getInitialValues = (data) => {
    const userObj = data?.user_details || (data?.user_id && typeof data.user_id === 'object' ? data.user_id : {});
    return {
      business_name: data?.business_name || '',
      owner_name: data?.owner_name || userObj.name || '',
      email: data?.email || userObj.email || '',
      phone_number: data?.phone_number || userObj.phone_number || '',
      country_code: data?.country_code || userObj.country_code || '91',
      location_id: data?.location_id?._id || data?.location_id || '',
      map_url: data?.map_url || '',
      address: data?.address || '',
      city: data?.city || '',
      pincode: data?.pincode || '',
      description: data?.description || '',
      latitude: data?.latitude || '',
      longitude: data?.longitude || '',
      price_for_two: data?.price_for_two || '',
      logo_url: data?.logo_url || '',
      businessHours: data?.businessHours || {
        days: [],
        openingTime: '',
        closingTime: ''
      },
      offered_activities: data?.offered_activities || [],
      newActivityName: '',
      newPackageName: ''
    };
  };

  const [role, setRole] = useState(initialData?.category || 'Restaurant');
  const [formData, setFormData] = useState(() => getInitialValues(initialData));
  const [files, setFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState(initialData?.image_url || []);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(initialData?.logo_url || '');
  const [activitiesList, setActivitiesList] = useState([]);

  useEffect(() => {
    if (initialData) {
      setRole(initialData.category || 'Restaurant');
      setFormData(getInitialValues(initialData));
      setImagePreviewUrls(initialData.image_url || []);
      setLogoPreviewUrl(initialData.logo_url || '');
      setLogoFile(null);
    }
  }, [initialData]);

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setFiles([]);
    setImagePreviewUrls([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl('');
    setFormData((prev) => ({ ...prev, logo_url: '' }));
  };

  const handleDaysChange = (day) => {
    setFormData((prev) => {
      const days = [...(prev.businessHours?.days || [])];
      if (days.includes(day)) {
        return {
          ...prev,
          businessHours: {
            ...prev.businessHours,
            days: days.filter((d) => d !== day)
          }
        };
      } else {
        return {
          ...prev,
          businessHours: {
            ...prev.businessHours,
            days: [...days, day]
          }
        };
      }
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    const previews = selectedFiles.map((file) => URL.createObjectURL(file));
    setImagePreviewUrls((prev) => [...prev, ...previews]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviewUrls((prev) => {
      const newUrls = [...prev];
      URL.revokeObjectURL(newUrls[index]);
      newUrls.splice(index, 1);
      return newUrls;
    });
  };

  const addActivity = () => {
    if (formData.newActivityName) {
      setActivitiesList([
        ...activitiesList,
        {
          name: formData.newActivityName,
          packages: formData.newPackageName ? [formData.newPackageName] : []
        }
      ]);
      setFormData((prev) => ({ ...prev, newActivityName: '', newPackageName: '' }));
    }
  };

  const removeActivity = (index) => {
    setActivitiesList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExtractCoordinates = () => {
    const coords = extractCoordinatesFromMapUrl(formData.map_url);
    if (coords) {
      setFormData((prev) => ({
        ...prev,
        latitude: coords.latitude,
        longitude: coords.longitude
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let uploadedUrls = [];
    if (files.length > 0) {
      uploadedUrls = await uploadImages(files);
    }

    let logoUrl = formData.logo_url || '';
    if (logoFile) {
      const uploadedLogo = await uploadImages([logoFile]);
      if (uploadedLogo && uploadedLogo.length > 0) {
        logoUrl = uploadedLogo[0];
      }
    }

    const submitData = {
      ...formData,
      role,
      logo_url: logoUrl,
      image_url: uploadedUrls
    };

    if (role === 'Activity') {
      submitData.description = submitData.description
        ? `${submitData.description}\n\nActivities:\n${JSON.stringify(activitiesList)}`
        : JSON.stringify(activitiesList);
    }

    const handleSuccess = () => {
      setFormData({
        business_name: '',
        owner_name: '',
        email: '',
        phone_number: '',
        country_code: '91',
        location_id: '',
        map_url: '',
        address: '',
        city: '',
        pincode: '',
        description: '',
        latitude: '',
        longitude: '',
        price_for_two: '',
        logo_url: '',
        businessHours: { days: [], openingTime: '', closingTime: '' },
        newActivityName: '',
        newPackageName: ''
      });
      setFiles([]);
      setImagePreviewUrls([]);
      setLogoFile(null);
      setLogoPreviewUrl('');
      setActivitiesList([]);
      if (onSuccess) onSuccess();
    };

    if (initialData) {
      await updatePartner(initialData._id, submitData, handleSuccess);
    } else {
      await registerPartner(submitData, handleSuccess);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 w-full mx-auto animate-tab-switch">
      {/* 1. Category Selector */}
      <CategorySelector selectedRole={role} onRoleChange={handleRoleChange} />

      {/* 2. Business Logo */}
      <BusinessLogoSection
        logoPreviewUrl={logoPreviewUrl}
        onLogoFileChange={handleLogoFileChange}
        onRemoveLogo={removeLogo}
      />

      {/* 3. Basic Info & Location */}
      <BasicInfoSection
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        locations={locations}
        isEdit={!!initialData}
        role={role}
        onExtractCoordinates={handleExtractCoordinates}
      />

      {/* 4. Role-Specific Details */}
      <CategoryDetails
        role={role}
        formData={formData}
        handleChange={handleChange}
        activitiesList={activitiesList}
        onAddActivity={addActivity}
        onRemoveActivity={removeActivity}
      />

      {/* 5. Business Hours (24h Clock & security validation intact) */}
      <BusinessHoursSection
        formData={formData}
        setFormData={setFormData}
        onDaysChange={handleDaysChange}
      />

      {/* 6. Media Gallery (at the end before submit) */}
      <MediaGallerySection
        imagePreviewUrls={imagePreviewUrls}
        onFileChange={handleFileChange}
        onRemoveFile={removeFile}
      />

      {/* 7. Submit Button */}
      <div className="pt-4 border-t border-slate-100 flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto px-6 py-3 bg-[#067978] hover:bg-[#056362] text-white rounded-2xl font-bold text-xs sm:text-sm shadow-md shadow-[#067978]/20 disabled:opacity-70 transition-all flex items-center justify-center gap-2 active:scale-95 cursor-pointer"
        >
          {isSubmitting
            ? initialData
              ? 'Updating...'
              : 'Registering...'
            : initialData
            ? 'Update Partner'
            : 'Register Partner'}
        </button>
      </div>
    </form>
  );
};

export default AddPartnerForm;
